import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import {
  Laptop, CheckCircle2, Wrench, PackageOpen, ShieldAlert, Clock,
  Ticket, AlertTriangle, UserPlus, ArrowRight, X, ListChecks,
} from 'lucide-react';

const KPI = ({ icon: Icon, label, value, tone = 'default', onClick, testId }) => {
  const tones = {
    default: 'bg-white border-slate-200', blue: 'bg-teal-50 border-teal-200',
    green: 'bg-emerald-50 border-emerald-200', amber: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200',
  };
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testId}
      className={`text-left rounded-sm border p-4 transition-shadow ${tones[tone]} ${onClick ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`}>
      <div className="flex items-center gap-2 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500"><Icon size={13} /> {label}</div>
      <div className="text-2xl font-display font-extrabold mt-2 text-[#1F7A6E]">{value}</div>
    </button>
  );
};

export default function ITDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [checklistEmp, setChecklistEmp] = useState(null);

  const load = useCallback(() => {
    apiGet('/it/assets/dashboard').then(setD).catch(() => setD({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="p-8 text-slate-500" data-testid="it-dash-loading">Loading…</div>;

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="it-dashboard">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Information Technology</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">IT Dashboard</h1>
        <p className="text-sm text-slate-600 mt-1">Technology assets, allocations and service requests across the company.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Laptop} label="Total Devices" value={d.total_assets || 0} onClick={() => nav('/app/it/assets')} testId="kpi-total" />
        <KPI icon={CheckCircle2} label="Allocated" value={d.allocated_count || 0} tone="blue" onClick={() => nav('/app/it/allocations')} testId="kpi-allocated" />
        <KPI icon={PackageOpen} label="Available" value={d.available_count || 0} tone="green" onClick={() => nav('/app/it/assets')} testId="kpi-available" />
        <KPI icon={Wrench} label="In Repair" value={d.in_repair_count || 0} tone="amber" testId="kpi-repair" />
      </div>

      {/* New joiners awaiting IT setup */}
      {d.pending_joiners > 0 && (
        <button onClick={() => setChecklistEmp('list')} data-testid="onboarding-prompt"
          className="w-full text-left bg-teal-50 border-2 border-teal-200 rounded-sm p-4 flex items-center gap-3 hover:shadow-md">
          <UserPlus size={22} className="text-teal-600 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-sm text-teal-900">{d.pending_joiners} new joiner{d.pending_joiners > 1 ? 's' : ''} awaiting IT setup</div>
            <div className="text-xs text-teal-700 mt-0.5">Allocate devices, create email, assign SIM — click to open the IT onboarding checklist.</div>
          </div>
          <ArrowRight size={16} className="text-teal-500" />
        </button>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KPI icon={Ticket} label="Open Requests" value={d.open_requests || 0} tone={d.open_requests ? 'amber' : 'default'} onClick={() => nav('/app/it/requests')} testId="kpi-open-reqs" />
        <KPI icon={AlertTriangle} label="Urgent Requests" value={d.urgent_requests || 0} tone={d.urgent_requests ? 'red' : 'default'} onClick={() => nav('/app/it/requests')} testId="kpi-urgent-reqs" />
        <KPI icon={Clock} label="Overdue Returns" value={d.overdue_count || 0} tone={d.overdue_count ? 'red' : 'default'} onClick={() => nav('/app/it/allocations')} testId="kpi-overdue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="warranty-card">
          <div className="px-4 py-3 border-b"><h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><ShieldAlert size={14} /> Warranty Expiring (60 days)</h3></div>
          {(d.warranty_expiring_60d || []).length === 0 ? <div className="p-4 text-xs text-slate-400">No warranties expiring soon.</div> : (
            <div className="divide-y divide-slate-100">
              {d.warranty_expiring_60d.map((a) => (
                <div key={a.id} className="px-4 py-2 flex items-center justify-between text-xs" data-testid={`warranty-${a.id}`}>
                  <span><span className="font-mono text-[10px] text-slate-400">{a.asset_tag}</span> <span className="font-display font-bold">{a.name}</span></span>
                  <span className="text-amber-600 font-bold">{a.warranty_expiry}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm" data-testid="overdue-card">
          <div className="px-4 py-3 border-b"><h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><Clock size={14} /> Overdue Returns</h3></div>
          {(d.overdue_returns || []).length === 0 ? <div className="p-4 text-xs text-slate-400">No overdue returns. ✓</div> : (
            <div className="divide-y divide-slate-100">
              {d.overdue_returns.map((al) => (
                <div key={al.id} className="px-4 py-2 flex items-center justify-between text-xs" data-testid={`overdue-${al.id}`}>
                  <span><span className="font-display font-bold">{al.asset_name}</span> <span className="text-slate-400">· {al.employee_name}</span></span>
                  <span className="text-red-600 font-bold">due {al.expected_return_date}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {checklistEmp && <OnboardingPanel onClose={() => { setChecklistEmp(null); load(); }} />}
    </div>
  );
}

function OnboardingPanel({ onClose }) {
  const nav = useNavigate();
  const [joiners, setJoiners] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { apiGet('/it/onboarding/pending-joiners').then((r) => setJoiners(r.joiners || [])).catch(() => setJoiners([])); }, []);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="onboarding-panel">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-lg max-h-[88vh] overflow-y-auto">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-display font-bold text-sm flex items-center gap-2"><ListChecks size={16} /> IT Onboarding — New Joiners</h3>
          <button onClick={onClose} data-testid="onboarding-close"><X size={18} /></button>
        </div>
        {active ? (
          <JoinerChecklist emp={active} onBack={() => setActive(null)} />
        ) : (
          <div className="divide-y divide-slate-100">
            {joiners.length === 0 ? <div className="p-5 text-sm text-slate-400">No pending joiners.</div> : joiners.map((j) => (
              <button key={j.id} onClick={() => setActive(j)} data-testid={`joiner-${j.id}`}
                className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-slate-50">
                <div>
                  <div className="font-display font-bold text-sm">{j.name}</div>
                  <div className="text-xs text-slate-500">{j.emp_id} · {j.department || '—'}</div>
                </div>
                <div className="text-xs font-bold text-[#1F7A6E]">{j.checklist_done}/{j.checklist_total} <ArrowRight size={12} className="inline" /></div>
              </button>
            ))}
          </div>
        )}
        <div className="px-5 py-3 border-t">
          <button onClick={() => { onClose(); nav('/app/it/assets'); }} className="text-xs text-[#1F7A6E] font-bold">Go to Asset Register to allocate devices →</button>
        </div>
      </div>
    </div>
  );
}

function JoinerChecklist({ emp, onBack }) {
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet(`/it/onboarding/${emp.id}/checklist`).then((r) => { setData(r); setProgress(r.progress || {}); }).catch(() => {});
  }, [emp.id]);

  const toggle = (item) => setProgress((p) => ({ ...p, [item]: !p[item] }));
  const save = async () => {
    setBusy(true);
    try { await apiPut(`/it/onboarding/${emp.id}/checklist`, { progress }); toast.success('Checklist saved'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-5 text-sm text-slate-400">Loading…</div>;
  return (
    <div className="p-5 space-y-3" data-testid="joiner-checklist">
      <button onClick={onBack} className="text-xs text-slate-500">← Back to joiners</button>
      <div className="font-display font-bold text-sm">{emp.name} <span className="text-slate-400 font-normal">· {emp.emp_id}</span></div>
      <div className="space-y-2">
        {(data.items || []).map((item) => (
          <label key={item} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`checklist-item-${item.replace(/\s+/g, '-')}`}>
            <input type="checkbox" checked={!!progress[item]} onChange={() => toggle(item)} className="accent-[#1F7A6E]" />
            <span className={progress[item] ? 'line-through text-slate-400' : ''}>{item}</span>
          </label>
        ))}
      </div>
      <button onClick={save} disabled={busy} data-testid="checklist-save" className="w-full py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50">{busy ? 'Saving…' : 'Save Progress'}</button>
    </div>
  );
}
